import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, X } from "lucide-react";
import { ANALYTICS_EVENTS, trackEvent } from "utils/analytics";
import { PanelSection } from "../PanelSection";
import { useSectionDisclosure } from "../useSectionDisclosure";
import { buildSourceRoster } from "./buildSourceRoster";
import { SourceRow } from "./SourceRow";
import type { SourceListProps } from "./types";

export const SourceList = ({
  feedsByBoard,
  typesByCategory,
  onSaveFeeds,
  baseSources,
  addedSources,
  removedSources,
  sourceNames,
  feedingBoardByCircuit,
  lengthsByCircuit,
  formatMetres,
  onRename,
  onRemoveTo,
  onRemove,
  onRestore,
  onAddSource,
  isPlacingSource = false,
  areaCountByLabel,
  onFeedArea,
  onRemoveAreas,
  isOpen,
  onOpenChange,
}: SourceListProps) => {
  const { t } = useTranslation();

  const disclosure = useSectionDisclosure({ isOpen, onOpenChange });
  const [draftName, setDraftName] = useState<string | null>(null);

  const roster = useMemo(
    () =>
      buildSourceRoster({
        baseSources,
        addedSources,
        removedSources,
        sourceNames,
        feedingBoardByCircuit,
        lengthsByCircuit,
      }),
    [
      addedSources,
      baseSources,
      feedingBoardByCircuit,
      lengthsByCircuit,
      removedSources,
      sourceNames,
    ],
  );

  const live = useMemo(
    () => roster.filter((entry) => !entry.isRemoved),
    [roster],
  );

  // A board carrying nothing is the shape of the RUN's mistake: it invented six
  // circuits off a device it took for a board, and every one read 0 m. A board
  // he placed himself also carries nothing until he re-points circuits at it —
  // counting his own here would make the chip cry wolf on his own work. The row
  // still says "0 circuits · 0 m" either way, so nothing is hidden.
  const emptyCount = useMemo(
    () =>
      live.filter((entry) => entry.circuitCount === 0 && !entry.isAdded).length,
    [live],
  );

  const takenNames = useMemo(
    () => new Set(roster.map((entry) => entry.name.trim().toLowerCase())),
    [roster],
  );

  const trimmedDraft = draftName?.trim() ?? "";
  // Two boards with one name are indistinguishable in the feed-point picker,
  // which addresses them by name and not by their marker label.
  const isDuplicate = takenNames.has(trimmedDraft.toLowerCase());
  const canPlace = trimmedDraft.length > 0 && !isDuplicate;

  const place = () => {
    if (!canPlace) return;
    trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_CABLE_FEED_POINT_CHANGED, {
      action: "added",
    });
    setDraftName(null);
    onAddSource?.(trimmedDraft);
  };

  const startAdding = () => {
    disclosure.open();
    setDraftName("");
  };

  const targetsFor = (label: string) =>
    live
      .filter((entry) => entry.label !== label)
      .map((entry) => ({ value: entry.label, label: entry.name }));

  if (roster.length === 0) return null;

  return (
    <PanelSection
      tone="neutral"
      title={t("smartSurveyor.cablesBeta.sourcesTitle")}
      count={live.length}
      isOpen={disclosure.isOpen}
      onToggle={disclosure.toggle}
      action={
        onAddSource && (
          // In the header, not the body: collapsed is right for nine rows, but
          // he cannot find a way to CREATE a board behind a disclosure he has
          // no reason to open.
          <button
            type="button"
            onClick={startAdding}
            title={t("smartSurveyor.cablesBeta.sourceAdd")}
            className="mr-1.5 grid h-9 w-9 flex-none place-items-center rounded-md border border-transparent text-slate-500 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700"
          >
            <Plus size={18} />
          </button>
        )
      }
    >
      {emptyCount > 0 && (
        <p className="mb-1.5 text-[13px] text-slate-600">
          {t("smartSurveyor.cablesBeta.sourcesFeedingNothing", {
            count: emptyCount,
          })}
        </p>
      )}
      {draftName !== null && !isPlacingSource && (
        <div className="mb-1.5 rounded-md border border-blue-300 bg-blue-50 p-2">
          <label className="block text-[13px] font-medium text-slate-800">
            {t("smartSurveyor.cablesBeta.sourceAddName")}
          </label>
          <input
            autoFocus
            value={draftName}
            aria-label={t("smartSurveyor.cablesBeta.sourceAddName")}
            onChange={(event) => setDraftName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") place();
              if (event.key === "Escape") setDraftName(null);
            }}
            className="mt-1 h-9 w-full rounded border border-slate-300 px-2 text-[14px] text-slate-800 focus:border-blue-500 focus:outline-none"
          />
          {isDuplicate && (
            <p className="mt-1 text-[13px] font-medium text-red-700">
              {t("smartSurveyor.cablesBeta.sourceAddNameTaken")}
            </p>
          )}
          <div className="mt-1.5 flex items-center gap-2">
            <button
              type="button"
              onClick={place}
              disabled={!canPlace}
              className="flex h-9 flex-none items-center gap-1.5 rounded-md bg-blue-600 px-2.5 text-[13px] font-semibold text-white hover:bg-blue-700 disabled:bg-slate-300"
            >
              <Plus size={15} />
              {t("smartSurveyor.cablesBeta.sourceAddPlace")}
            </button>
            <button
              type="button"
              onClick={() => setDraftName(null)}
              className="flex h-9 flex-none items-center gap-1 px-1 text-[13px] font-medium text-slate-600 hover:text-slate-900"
            >
              <X size={14} />
              {t("smartSurveyor.cablesBeta.cancel")}
            </button>
          </div>
        </div>
      )}
      <ul className="space-y-1.5">
        {roster.map((entry) => (
          <SourceRow
            key={entry.label}
            entry={entry}
            areaCount={areaCountByLabel?.[entry.label] ?? 0}
            onFeedArea={onFeedArea}
            onRemoveAreas={onRemoveAreas}
            moveTargets={targetsFor(entry.label)}
            formatMetres={formatMetres}
            onRename={onRename}
            onRemoveTo={onRemoveTo}
            onRemove={onRemove}
            onRestore={onRestore}
            feeds={
              feedsByBoard?.[entry.label] && onSaveFeeds
                ? {
                    resolved: feedsByBoard[entry.label],
                    typesByCategory: typesByCategory ?? {},
                    onSave: (feed) => onSaveFeeds(entry.label, feed),
                  }
                : undefined
            }
          />
        ))}
      </ul>
    </PanelSection>
  );
};
